import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { businessApi } from '../services/api';
import { Business } from '../types';
import axios from 'axios';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://fitpulse.es/api';

interface ReminderSettings {
  enabled24h: boolean;
  enabled1h: boolean;
  message24h: string;
  message1h: string;
}

const DEFAULT: ReminderSettings = {
  enabled24h: true,
  enabled1h: false,
  message24h: 'Te recordamos que mañana tienes un evento. ¡Te esperamos puntual!',
  message1h: 'Tu evento empieza en 1 hora. ¡No llegues tarde!',
};

export function Reminders() {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [selectedBiz, setSelectedBiz] = useState<Business | null>(null);
  const [settings, setSettings] = useState<ReminderSettings>(DEFAULT);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const r = await businessApi.getMyBusinesses();
      const list = r.data.data.businesses || [];
      setBusinesses(list);
      if (list.length > 0) selectBusiness(list[0]);
    } catch { toast.error('Error cargando negocios'); }
    finally { setIsLoading(false); }
  };

  const selectBusiness = (b: Business) => {
    setSelectedBiz(b);
    setSettings({ ...DEFAULT, ...((b as any).reminderSettings || {}) });
  };

  const handleSave = async () => {
    if (!selectedBiz) return;
    setIsSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.patch(`${API_URL}/businesses/${selectedBiz.id}`,
        { reminderSettings: settings },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('✅ Configuración guardada');
    } catch { toast.error('Error al guardar'); }
    finally { setIsSaving(false); }
  };

  if (isLoading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>
  );

  return (
    <div className="space-y-6">
      <style>{`
        .reminder-card { background: white; border-radius: 16px; border: 1px solid #e2e8f0; overflow: hidden; }
        .reminder-header { padding: 20px 24px; border-bottom: 1px solid #f1f5f9; display: flex; align-items: center; justify-content: space-between; }
        .reminder-body { padding: 24px; }
        .toggle-row { display: flex; align-items: center; justify-content: space-between; padding: 16px 0; border-bottom: 1px solid #f8fafc; }
        .toggle { position: relative; width: 48px; height: 26px; background: #e2e8f0; border-radius: 100px; cursor: pointer; transition: background .3s; border: none; flex-shrink: 0; }
        .toggle.on { background: #3b82f6; }
        .toggle-knob { position: absolute; top: 3px; left: 3px; width: 20px; height: 20px; background: white; border-radius: 50%; transition: transform .3s; box-shadow: 0 1px 3px rgba(0,0,0,.2); }
        .toggle.on .toggle-knob { transform: translateX(22px); }
        .preview-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; margin-top: 12px; }
        .preview-label { font-size: .68rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: .08em; margin-bottom: 8px; }
      `}</style>

      {/* HEADER */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">📢 Recordatorios</h1>
          <p className="text-secondary-500 text-sm mt-1">Configura los recordatorios automáticos para tus clientes</p>
        </div>
        {businesses.length > 1 && (
          <select className="input max-w-xs" value={selectedBiz?.id || ''} onChange={e => {
            const b = businesses.find(b => b.id === e.target.value);
            if (b) selectBusiness(b);
          }}>
            {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
      </div>

      {/* INFO */}
      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: '14px 18px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>💡</span>
        <div style={{ fontSize: '.85rem', color: '#1d4ed8' }}>
          <p style={{ fontWeight: 700, marginBottom: 4 }}>¿Cómo funcionan los recordatorios automáticos?</p>
          <p>FitPulse enviará automáticamente un email a todos los clientes inscritos en tus eventos según la configuración que elijas. Los emails se envían desde <strong>info@fitpulse.es</strong> en nombre de tu negocio.</p>
        </div>
      </div>

      {/* RECORDATORIO 24H */}
      <div className="reminder-card">
        <div className="reminder-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>📅</div>
            <div>
              <p style={{ fontWeight: 700, fontSize: '.95rem', color: '#1e293b' }}>Recordatorio 24 horas antes</p>
              <p style={{ fontSize: '.78rem', color: '#64748b' }}>Se envía el día anterior al evento</p>
            </div>
          </div>
          <button className={`toggle ${settings.enabled24h ? 'on' : ''}`} onClick={() => setSettings(s => ({ ...s, enabled24h: !s.enabled24h }))}>
            <div className="toggle-knob"></div>
          </button>
        </div>
        {settings.enabled24h && (
          <div className="reminder-body">
            <label className="label mb-2">Mensaje del recordatorio</label>
            <textarea
              className="input"
              rows={3}
              value={settings.message24h}
              onChange={e => setSettings(s => ({ ...s, message24h: e.target.value }))}
              placeholder="Ej: Recuerda que mañana tienes tu evento. ¡Te esperamos!"
              maxLength={500}
            />
            <p style={{ fontSize: '.72rem', color: '#94a3b8', marginTop: 4 }}>{settings.message24h.length}/500</p>
            <div className="preview-box">
              <div className="preview-label">Vista previa del email</div>
              <p style={{ fontSize: '.82rem', color: '#475569', lineHeight: 1.6 }}>
                Hola <strong>[Nombre cliente]</strong>, tienes un evento próximo:<br/>
                <strong>[Nombre evento]</strong> · [Fecha] · [Hora]<br/><br/>
                <em>{settings.message24h}</em>
              </p>
            </div>
          </div>
        )}
      </div>

      {/* RECORDATORIO 1H */}
      <div className="reminder-card">
        <div className="reminder-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>⏰</div>
            <div>
              <p style={{ fontWeight: 700, fontSize: '.95rem', color: '#1e293b' }}>Recordatorio 1 hora antes</p>
              <p style={{ fontSize: '.78rem', color: '#64748b' }}>Se envía 1 hora antes de que empiece el evento</p>
            </div>
          </div>
          <button className={`toggle ${settings.enabled1h ? 'on' : ''}`} onClick={() => setSettings(s => ({ ...s, enabled1h: !s.enabled1h }))}>
            <div className="toggle-knob"></div>
          </button>
        </div>
        {settings.enabled1h && (
          <div className="reminder-body">
            <label className="label mb-2">Mensaje del recordatorio</label>
            <textarea
              className="input"
              rows={3}
              value={settings.message1h}
              onChange={e => setSettings(s => ({ ...s, message1h: e.target.value }))}
              placeholder="Ej: ¡Tu evento empieza en 1 hora! No olvides traer tu equipamiento."
              maxLength={500}
            />
            <p style={{ fontSize: '.72rem', color: '#94a3b8', marginTop: 4 }}>{settings.message1h.length}/500</p>
            <div className="preview-box">
              <div className="preview-label">Vista previa del email</div>
              <p style={{ fontSize: '.82rem', color: '#475569', lineHeight: 1.6 }}>
                Hola <strong>[Nombre cliente]</strong>, tu evento empieza pronto:<br/>
                <strong>[Nombre evento]</strong> · [Fecha] · [Hora]<br/><br/>
                <em>{settings.message1h}</em>
              </p>
            </div>
          </div>
        )}
      </div>

      {/* RECORDATORIO MANUAL */}
      <div className="reminder-card">
        <div className="reminder-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>✍️</div>
            <div>
              <p style={{ fontWeight: 700, fontSize: '.95rem', color: '#1e293b' }}>Recordatorio manual</p>
              <p style={{ fontSize: '.78rem', color: '#64748b' }}>Envía un mensaje personalizado cuando quieras desde cada evento</p>
            </div>
          </div>
          <span style={{ background: '#dcfce7', color: '#15803d', fontSize: '.72rem', fontWeight: 700, padding: '4px 12px', borderRadius: 100 }}>Siempre activo</span>
        </div>
        <div className="reminder-body">
          <p style={{ fontSize: '.85rem', color: '#475569', lineHeight: 1.6 }}>
            Puedes enviar un recordatorio manual desde <strong>Eventos → 📢</strong> en cada evento. Escribe el mensaje que quieras y se enviará a todos los inscritos al momento.
          </p>
          <a href="/events" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 14, background: '#f8fafc', border: '1px solid #e2e8f0', color: '#1e293b', fontWeight: 600, fontSize: '.85rem', padding: '9px 16px', borderRadius: 10, textDecoration: 'none' }}>
            📅 Ir a Eventos →
          </a>
        </div>
      </div>

      {/* GUARDAR */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={handleSave} disabled={isSaving} className="btn-primary" style={{ minWidth: 160 }}>
          {isSaving ? 'Guardando...' : '💾 Guardar configuración'}
        </button>
      </div>
    </div>
  );
}
